import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Shield,
  Send,
  Megaphone,
  Smartphone,
  Repeat,
  Code2,
} from "lucide-react";

const smsSolutions = [
  {
    id: 1,
    icon: Shield,
    title: "OTP SMS",
    heading: "Instant OTP Verification",
    description:
      "Deliver secure one-time passwords for authentication, registration, payment confirmation, and password recovery with lightning-fast delivery.",
    features: [
      "Instant Delivery",
      "Bank Grade Security",
      "Global Coverage",
      "High Success Rate",
    ],
  },
  {
    id: 2,
    icon: Send,
    title: "Transactional SMS",
    heading: "Real-Time Customer Notifications",
    description:
      "Automatically notify customers about orders, payments, account activity, shipping updates, appointments, and important business events.",
    features: [
      "Order Updates",
      "Payment Alerts",
      "Appointment Reminders",
      "Delivery Notifications",
    ],
  },
  {
    id: 3,
    icon: Megaphone,
    title: "Promotional SMS",
    heading: "Marketing Campaigns That Convert",
    description:
      "Launch personalized marketing campaigns, discount offers, seasonal promotions, and customer engagement campaigns using enterprise messaging.",
    features: [
      "Festival Campaigns",
      "Discount Offers",
      "Bulk Campaigns",
      "Scheduling",
    ],
  },
  {
    id: 4,
    icon: Smartphone,
    title: "Bulk SMS",
    heading: "Communicate at Scale",
    description:
      "Send thousands of SMS messages instantly while maintaining exceptional delivery performance and enterprise-grade reliability.",
    features: [
      "Mass Messaging",
      "Smart Scheduling",
      "High Throughput",
      "Delivery Reports",
    ],
  },
  {
    id: 5,
    icon: Repeat,
    title: "2-Way SMS",
    heading: "Interactive Conversations",
    description:
      "Allow customers to reply directly to your SMS, enabling meaningful communication and improving customer engagement.",
    features: [
      "Customer Replies",
      "Feedback Collection",
      "Live Support",
      "Better Engagement",
    ],
  },
  {
    id: 6,
    icon: Code2,
    title: "SMS API",
    heading: "Easy API Integration",
    description:
      "Integrate SMS services into websites, CRMs, ERPs, mobile apps, and enterprise software through robust REST APIs.",
    features: [
      "REST API",
      "Webhook Support",
      "Developer Friendly",
      "Secure Authentication",
    ],
  },
];

export default function HeroSection() {
  const [activeSolution, setActiveSolution] = useState(smsSolutions[0]);

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-10
        font-saira
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          absolute
          left-0
          top-0
          h-[420px]
          w-[420px]
          rounded-full
          bg-orange-500/[0.06]
          blur-[160px]
        "
      />

      <div
        className="
          absolute
          bottom-0
          right-0
          h-[420px]
          w-[420px]
          rounded-full
          bg-orange-500/[0.06]
          blur-[160px]
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          max-w-7xl
          px-6
          lg:px-8
        "
      >
        {/* =====================================================
            HEADING
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
          className="max-w-4xl"
        >
          {/* Badge */}

          <span
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-orange-500/25
              bg-orange-500/10
              px-4
              py-2
              text-xs
              font-semibold
              uppercase
              tracking-[3px]
              text-[var(--brand-light)]
            "
          >
            SMS Solutions
          </span>

          {/* Heading */}

          <h2
            className="
              mt-5
              text-3xl
              font-bold
              leading-tight
              text-[var(--text-primary)]
              lg:text-5xl
            "
          >
            Multi-Channel SMS Solutions Designed
            <br />
            for Every Business
          </h2>

          {/* Description */}

          <p
            className="
              mt-5
              max-w-5xl
              text-base
              leading-7
              text-[var(--text-muted)]
              lg:text-lg
            "
          >
            From OTP authentication and transactional alerts to promotional
            campaigns and two-way messaging, choose the right messaging
            solution for every stage of your customer communication.
          </p>
        </motion.div>

        {/* =====================================================
            CONTENT
        ====================================================== */}

        <div
          className="
            mt-12
            grid
            items-start
            gap-8
            lg:grid-cols-[1.45fr_.85fr]
          "
        >
          {/* =====================================================
              LEFT GRID
          ====================================================== */}

          <div
            className="
              grid
              grid-cols-2
              gap-4
              lg:grid-cols-3
            "
          >
            {smsSolutions.map((item) => {
              const Icon = item.icon;
              const active = activeSolution.id === item.id;

              return (
                <motion.button
                  key={item.id}
                  whileHover={{
                    y: -5,
                    scale: 1.02,
                  }}
                  whileTap={{
                    scale: 0.98,
                  }}
                  onClick={() => setActiveSolution(item)}
                  className={`
                    group
                    relative
                    overflow-hidden
                    rounded-3xl
                    border
                    transition-all
                    duration-300

                    ${
                      active
                        ? "border-[var(--brand-primary)] bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-hover)] shadow-[0_22px_50px_rgba(249,115,22,.22)]"
                        : "border-[var(--border-primary)] bg-[var(--card-bg)] hover:border-orange-500/40 hover:bg-[var(--card-bg-secondary)]"
                    }
                  `}
                >
                  {/* Active Card Glow */}

                  {active && (
                    <div
                      className="
                        absolute
                        inset-0
                        bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,.18),transparent_60%)]
                      "
                    />
                  )}

                  {/* Card Content */}

                  <div
                    className="
                      relative
                      flex
                      h-[165px]
                      flex-col
                      justify-between
                      p-5
                    "
                  >
                    {/* Icon */}

                    <div
                      className={`
                        flex
                        h-12
                        w-12
                        items-center
                        justify-center
                        rounded-2xl

                        ${
                          active
                            ? "bg-white/15"
                            : "border border-orange-500/10 bg-orange-500/10"
                        }
                      `}
                    >
                      <Icon
                        size={26}
                        className={
                          active
                            ? "text-white"
                            : "text-[var(--brand-light)]"
                        }
                      />
                    </div>

                    {/* Card Text */}

                    <div className="text-left">
                      <h3
                        className="
                          text-lg
                          font-semibold
                          text-[var(--text-primary)]
                        "
                      >
                        {item.title}
                      </h3>

                      <p
                        className={`
                          mt-1
                          text-sm
                          leading-5

                          ${
                            active
                              ? "text-orange-50"
                              : "text-[var(--text-muted)]"
                          }
                        `}
                      >
                        {item.heading}
                      </p>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* =====================================================
              RIGHT DETAILS CARD
          ====================================================== */}

          <motion.div
            key={activeSolution.id}
            initial={{
              opacity: 0,
              x: 25,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.35,
            }}
            className="
              relative
              flex
              h-full
              flex-col
              overflow-hidden
              rounded-[28px]
              border
              border-[var(--border-primary)]
              bg-gradient-to-br
              from-[var(--card-bg-secondary)]
              to-[var(--bg-secondary)]
              p-7
              lg:p-8
            "
          >
            {/* =================================================
                DETAILS CARD GLOW
            ================================================== */}

            <div
              className="
                absolute
                -right-24
                -top-24
                h-64
                w-64
                rounded-full
                bg-orange-500/10
                blur-[120px]
              "
            />

            <div
              className="
                relative
                flex
                h-full
                flex-col
              "
            >
              {/* Heading */}

              <h3
                className="
                  text-[28px]
                  font-bold
                  leading-tight
                  text-[var(--text-primary)]
                "
              >
                {activeSolution.heading}
              </h3>

              {/* Orange Divider */}

              <div
                className="
                  mt-4
                  h-1
                  w-14
                  rounded-full
                  bg-[var(--brand-primary)]
                "
              />

              {/* Description */}

              <p
                className="
                  mt-5
                  text-base
                  leading-7
                  text-[var(--text-muted)]
                "
              >
                {activeSolution.description}
              </p>

              {/* =================================================
                  FEATURES
              ================================================== */}

              <div
                className="
                  mt-6
                  grid
                  grid-cols-2
                  gap-x-6
                  gap-y-4
                  border-t
                  border-[var(--border-secondary)]
                  pt-6
                "
              >
                {activeSolution.features.map((feature, index) => (
                  <div
                    key={index}
                    className="
                      flex
                      items-center
                      gap-3
                      text-sm
                      text-[var(--text-secondary)]
                    "
                  >
                    <CheckCircle2
                      size={18}
                      className="
                        shrink-0
                        text-[var(--brand-light)]
                      "
                    />

                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}